import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Switch,
    CircularProgress,
    Alert,
    Paper,
    useTheme
} from "@mui/material";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";

const AppConfig = () => {
    const theme = useTheme();
    const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
    const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(null);

    useEffect(() => {
        fetchAppConfig();
    }, []);

    const fetchAppConfig = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${ipconfig}/appconfig`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            if (response.data.status) {
                setSubscriptionEnabled(response.data.data.subscriptionEnabled || false);
                setAnalyticsEnabled(response.data.data.analyticsEnabled || false);
            }
        } catch (error: any) {
            setAlert({
                type: "error",
                message: error.response?.data?.msg || "Failed to fetch configuration",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (type: 'subscription' | 'analytics', checked: boolean) => {
        try {
            setUpdating(true);
            const payload = type === 'subscription' 
                ? { subscriptionEnabled: checked } 
                : { analyticsEnabled: checked };

            const response = await axios.put(
                `${ipconfig}/appconfig/update`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            if (response.data.status) {
                if (type === 'subscription') {
                    setSubscriptionEnabled(checked);
                } else {
                    setAnalyticsEnabled(checked);
                }
                
                setAlert({
                    type: "success",
                    message: `${type === 'subscription' ? 'Subscription' : 'Analytics'} system ${checked ? "enabled" : "disabled"} successfully`,
                });
                setTimeout(() => setAlert(null), 3000);
            }
        } catch (error: any) {
            setAlert({
                type: "error",
                message: error.response?.data?.msg || "Failed to update configuration",
            });
            setTimeout(() => setAlert(null), 3000);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                paddingX: "20px",
                paddingY: "20px",
            }}
        >
            <Typography
                sx={{
                    fontSize: "24px",
                    fontWeight: 600,
                    mb: 3,
                    color: "text.primary",
                }}
            >
                Application Configuration
            </Typography>

            {alert && (
                <Alert severity={alert.type} sx={{ mb: 3, maxWidth: "600px" }}>
                    {alert.message}
                </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: "600px" }}>
                <Paper
                    sx={{
                        p: 4,
                        bgcolor: "background.paper",
                        borderRadius: "10px",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: theme.palette.mode === "light" ? "0px 1.5px 6px rgba(0, 0, 0, 0.08)" : "none",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "18px",
                                    fontWeight: 600,
                                    mb: 0.5,
                                    color: "text.primary",
                                }}
                            >
                                Subscription System
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    color: "text.secondary",
                                }}
                            >
                                Enable or disable the subscription system globally for all users
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {updating && <CircularProgress size={20} />}
                            <Switch
                                checked={subscriptionEnabled}
                                onChange={(e) => handleToggle('subscription', e.target.checked)}
                                disabled={updating}
                                color="primary"
                            />
                        </Box>
                    </Box>
                </Paper>

                <Paper
                    sx={{
                        p: 4,
                        bgcolor: "background.paper",
                        borderRadius: "10px",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: theme.palette.mode === "light" ? "0px 1.5px 6px rgba(0, 0, 0, 0.08)" : "none",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "18px",
                                    fontWeight: 600,
                                    mb: 0.5,
                                    color: "text.primary",
                                }}
                            >
                                Analytics System
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    color: "text.secondary",
                                }}
                            >
                                Enable or disable the analytics system globally for all users
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {updating && <CircularProgress size={20} />}
                            <Switch
                                checked={analyticsEnabled}
                                onChange={(e) => handleToggle('analytics', e.target.checked)}
                                disabled={updating}
                                color="primary"
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default AppConfig;
